import { apiSlice } from './apiSlice'
import type { QualitySampling } from './qualitySamplingApi'

/** Combo option — mirrors BE SamplingLookupItem. */
export interface SamplingLookupItem {
  value: string
  label: string
}
export interface SamplingLookupResponse {
  items: SamplingLookupItem[]
  total: number
}

/** Payload for New/Edit/Copy — camelCase aliases of QualitySamplingWrite. */
export interface SamplingWrite {
  sampleNo?: string | null
  sampleType?: string | null
  sampleStatus?: string | null
  statusDate?: string | null
  statusReason?: string | null
  consigneeOptional?: string | null
  tlcContractRef?: string | null
  psDate?: string | null
  doNo?: string | null
  warehouseCode?: string | null
  lotId?: string | null
  lotSizeMt?: number | null
  licenseCertNo?: string | null
  clientLotId?: string | null
  origin?: string | null
  weightPerContMt?: number | null
  certUnit?: string | null
  grade?: string | null
  qtyPreshipmentClient?: number | null
  qtyPreshipmentSupervisor?: number | null
  cost?: number | null
  qtyAftershipmentClient?: number | null
  qtyAftershipmentSupervisor?: number | null
  receiver?: string | null
  inspectionBy?: string | null
  note?: string | null
  psSentDate?: string | null
  receivingAuthority?: string | null
  sealNo?: string | null
  resultReceiptDate?: string | null
  rejectReason?: string | null
  clientSampleRef?: string | null
  result?: string | null
  loadingType?: string | null
  note2?: string | null
  fumigationNote?: string | null
}

interface MutationResponse {
  id: number
  sampleNo: string | null
}
interface SuggestResponse {
  sampleNo: string
}
interface CheckResponse {
  exists: boolean
}
interface FormResponse {
  record: QualitySampling
}
/** Snapshot of latest prior sampling row for a contract (Access id2=0 cascade). */
export interface SamplingContractSnapshot {
  psDate?: string | null
  warehouseCode?: string | null
  lotSizeMt?: number | null
  weightPerContMt?: number | null
  licenseCertNo?: string | null
  origin?: string | null
  psSentDate?: string | null
  receiver?: string | null
  receivingAuthority?: string | null
  inspectionBy?: string | null
  sealNo?: string | null
  loadingType?: string | null
  certUnit?: string | null
  grade?: string | null
}

const LK = '/quality-sampling-lookups'

const samplingFormApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Hydrate Edit/Copy
    getSamplingForm: builder.query<FormResponse, number>({
      query: (id) => ({ url: `/quality-samplings/${id}/form` }),
    }),
    // Suggest + duplicate check (race-safe)
    suggestSampleNo: builder.query<SuggestResponse, void>({
      query: () => ({ url: '/quality-samplings/suggest/sample-no' }),
    }),
    checkSampleNo: builder.query<CheckResponse, { sampleNo: string; excludeId?: number }>({
      query: ({ sampleNo, excludeId }) => ({
        url: '/quality-samplings/check/sample-no',
        params: { sampleNo, ...(excludeId ? { excludeId } : {}) },
      }),
    }),
    createSampling: builder.mutation<MutationResponse, SamplingWrite>({
      query: (body) => ({ url: '/quality-samplings', method: 'POST', body }),
      invalidatesTags: ['QualitySampling'],
    }),
    updateSampling: builder.mutation<MutationResponse, { id: number; body: SamplingWrite }>({
      query: ({ id, body }) => ({ url: `/quality-samplings/${id}`, method: 'PUT', body }),
      invalidatesTags: ['QualitySampling'],
    }),
    // Lookups
    lkSamplingTlcContracts: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/tlc-contracts`, params: q ? { q } : {} }),
    }),
    lkSamplingDoNos: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/do-nos`, params: q ? { q } : {} }),
    }),
    lkSamplingWarehouses: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/warehouses`, params: q ? { q } : {} }),
    }),
    lkSamplingLots: builder.query<SamplingLookupResponse, { warehouseCode?: string; q?: string }>({
      query: ({ warehouseCode, q }) => ({
        url: `${LK}/lots`,
        params: { ...(warehouseCode ? { warehouseCode } : {}), ...(q ? { q } : {}) },
      }),
    }),
    lkSamplingLicenses: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/licenses`, params: q ? { q } : {} }),
    }),
    lkSamplingGrades: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/grades`, params: q ? { q } : {} }),
    }),
    lkSamplingOrigins: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/origins`, params: q ? { q } : {} }),
    }),
    lkSamplingSampleTypes: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/sample-types`, params: q ? { q } : {} }),
    }),
    lkSamplingSampleStatuses: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/sample-statuses`, params: q ? { q } : {} }),
    }),
    lkSamplingCertUnit: builder.query<{ certUnit: string | null }, string>({
      query: (licenseCertNo) => ({ url: `${LK}/cert-unit`, params: { licenseCertNo } }),
    }),
    lkSamplingContractGrade: builder.query<{ grade: string | null }, string>({
      query: (tlcContractRef) => ({ url: `${LK}/contract-grade`, params: { tlcContractRef } }),
    }),
    // Access tlc_contract_ref_Change (id2=0): snapshot latest prior sampling row.
    lkSamplingContractSnapshot: builder.query<SamplingContractSnapshot, string>({
      query: (tlcContractRef) => ({ url: `${LK}/contract-snapshot`, params: { tlcContractRef } }),
    }),
  }),
})

export const {
  useLazyGetSamplingFormQuery,
  useLazySuggestSampleNoQuery,
  useLazyCheckSampleNoQuery,
  useCreateSamplingMutation,
  useUpdateSamplingMutation,
  useLazyLkSamplingCertUnitQuery,
  useLazyLkSamplingContractGradeQuery,
  useLazyLkSamplingContractSnapshotQuery,
  useLkSamplingTlcContractsQuery,
  useLkSamplingDoNosQuery,
  useLkSamplingWarehousesQuery,
  useLkSamplingLotsQuery,
  useLkSamplingLicensesQuery,
  useLkSamplingGradesQuery,
  useLkSamplingOriginsQuery,
  useLkSamplingSampleTypesQuery,
  useLkSamplingSampleStatusesQuery,
} = samplingFormApi

export default samplingFormApi
